import React, { useState } from 'react';
import { PlanGoal } from '../../types/athlete';
import { createPlanGoal, deletePlanGoal } from '../../api/athlete';
import { Droplets, Bike, PersonStanding, Dumbbell, Plus, X, Trash2 } from 'lucide-react';

interface PlanGoalsProps {
  planId: string;
  goals: PlanGoal[];
  onUpdate: () => void;
}

const disciplineIcons = {
  swim: <Droplets className="text-blue-400" size={18} />,
  bike: <Bike className="text-green-400" size={18} />,
  run: <PersonStanding className="text-orange-400" size={18} />,
  strength: <Dumbbell className="text-purple-400" size={18} />,
};

const PlanGoals: React.FC<PlanGoalsProps> = ({ planId, goals, onUpdate }) => {
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    discipline: 'swim' as 'swim' | 'bike' | 'run' | 'strength',
    target_type: 'distance' as 'distance' | 'time' | 'sessions',
    target_value: '',
    unit: 'km',
    deadline_date: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createPlanGoal({
        plan_id: planId,
        discipline: formData.discipline,
        target_type: formData.target_type,
        target_value: parseFloat(formData.target_value),
        unit: formData.unit,
        deadline_date: formData.deadline_date || null,
      });
      setShowModal(false);
      setFormData({
        discipline: 'swim',
        target_type: 'distance',
        target_value: '',
        unit: 'km',
        deadline_date: '',
      });
      onUpdate();
    } catch (error) {
      console.error('Error creating goal:', error);
    }
  };

  const handleDelete = async (goalId: string) => {
    if (window.confirm('Delete this goal?')) {
      try {
        await deletePlanGoal(goalId);
        onUpdate();
      } catch (error) {
        console.error('Error deleting goal:', error);
      }
    }
  };

  const unitOptions = {
    distance: ['km', 'miles'],
    time: ['minutes', 'hours'],
    sessions: ['sessions'],
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-lg">Goals</h3>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center space-x-1 px-3 py-1.5 bg-[#3b82f6] text-white text-sm rounded-lg hover:bg-[#60a5fa] transition-colors"
        >
          <Plus size={16} />
          <span>Add Goal</span>
        </button>
      </div>

      {goals.length === 0 ? (
        <div className="card-base p-6 text-center text-muted">
          <p className="mb-2">No goals set for this plan</p>
          <p className="text-sm">Add goals to track your progress</p>
        </div>
      ) : (
        <div className="space-y-3">
          {goals.map((goal) => {
            const progressPercent = goal.target_value > 0
              ? Math.min(100, Math.round((goal.progress_current / goal.target_value) * 100))
              : 0;

            return (
              <div key={goal.id} className="card-base p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    {disciplineIcons[goal.discipline]}
                    <span className="font-medium capitalize">{goal.discipline}</span>
                  </div>
                  <button
                    onClick={() => handleDelete(goal.id)}
                    className="p-1 text-red-400 hover:bg-red-500/10 rounded transition-colors"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>

                <div className="mb-2">
                  <div className="flex justify-between text-sm mb-1">
                    <span>{goal.progress_current.toLocaleString()} / {goal.target_value.toLocaleString()} {goal.unit}</span>
                    <span className="text-muted">{progressPercent}%</span>
                  </div>
                  <div className="h-2 bg-[#141921] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#3b82f6] rounded-full transition-all"
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between text-xs text-muted">
                  <span>Target: {goal.target_type}</span>
                  {goal.deadline_date && (
                    <span>Due: {new Date(goal.deadline_date).toLocaleDateString()}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="card-base w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-[#2d3748]">
              <h3 className="font-semibold">Add Goal</h3>
              <button
                onClick={() => setShowModal(false)}
                className="p-1 hover:bg-[#2d3748] rounded transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#e2e8f0] mb-2">
                  Discipline
                </label>
                <select
                  value={formData.discipline}
                  onChange={(e) => setFormData({ ...formData, discipline: e.target.value as any })}
                  className="w-full"
                >
                  <option value="swim">Swim</option>
                  <option value="bike">Bike</option>
                  <option value="run">Run</option>
                  <option value="strength">Strength</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#e2e8f0] mb-2">
                  Target Type
                </label>
                <select
                  value={formData.target_type}
                  onChange={(e) => {
                    const type = e.target.value as any;
                    setFormData({
                      ...formData,
                      target_type: type,
                      unit: unitOptions[type as keyof typeof unitOptions][0],
                    });
                  }}
                  className="w-full"
                >
                  <option value="distance">Distance</option>
                  <option value="time">Time</option>
                  <option value="sessions">Number of Sessions</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#e2e8f0] mb-2">
                  Target Value
                </label>
                <div className="flex space-x-2">
                  <input
                    type="number"
                    min="1"
                    step="any"
                    value={formData.target_value}
                    onChange={(e) => setFormData({ ...formData, target_value: e.target.value })}
                    className="flex-1"
                    placeholder="e.g., 50"
                    required
                  />
                  <select
                    value={formData.unit}
                    onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                    className="w-32"
                  >
                    {unitOptions[formData.target_type as keyof typeof unitOptions].map((unit) => (
                      <option key={unit} value={unit}>{unit}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#e2e8f0] mb-2">
                  Deadline (optional)
                </label>
                <input
                  type="date"
                  value={formData.deadline_date}
                  onChange={(e) => setFormData({ ...formData, deadline_date: e.target.value })}
                  className="w-full"
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2 bg-[#141921] text-[#e2e8f0] rounded-lg hover:bg-[#2d3748] transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-[#3b82f6] text-white rounded-lg hover:bg-[#60a5fa] transition-colors"
                >
                  Create Goal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanGoals;
